
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaHome, 
  FaCartPlus, 
  FaUserAlt, 
  FaBoxOpen, 
  FaSearch, 
  FaBars, 
  FaTimes,
  FaChevronDown,
  FaHistory
} from 'react-icons/fa';
import styles from './Navbar.module.css';
import logo from '../assets/logo.jpg';
import CartDrawer from './CartDrawer/CartDrawer';
import SearchBar from './SearchBar';
import { useCart } from '../context/CartContext';
import { useAuth } from '../auth/AuthContext';
import { useLoginModal } from '../context/LoginModalWrapper';
import CouponBanner from './CouponBanner';

const Navbar = () => {
  const { user, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMobileBanner, setShowMobileBanner] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { cartItems } = useCart();
  const { openLoginModal } = useLoginModal();
  const searchInputRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [isDesktopScrolled, setIsDesktopScrolled] = useState(false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      if (desktop) {
        setShowMobileSearch(true);
        document.body.style.paddingTop = '0';
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Desktop scroll handler for CouponBanner and navbar top
  useEffect(() => {
    if (!isDesktop) return;
    const handleScroll = () => {
      setIsDesktopScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDesktop]);

  useEffect(() => {
  if (isDesktop) return;

  const handleScroll = () => {
    const scrollY = window.scrollY;
    
    if (scrollY < 50) {
      setShowMobileSearch(true);
      setShowMobileBanner(true);
      setIsScrolled(false);
    } else {
      setShowMobileSearch(false);
      setShowMobileBanner(false);
      setIsScrolled(true);
    }
  };

  window.addEventListener('scroll', handleScroll);
  return () => window.removeEventListener('scroll', handleScroll);
}, [isDesktop]);


  // Add/remove body padding based on search visibility (MOBILE)
  useEffect(() => {
    if (isDesktop) return;

    if (showMobileSearch) {
      document.body.style.paddingTop = showMobileBanner ? '152px' : '112px';
    } else {
      document.body.style.paddingTop = showMobileBanner ? '80px' : '64px';
    }

    return () => {
      document.body.style.paddingTop = '0';
    };
  }, [showMobileSearch, showMobileBanner, isDesktop]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.user-dropdown')) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Handle body overflow when cart is open
  useEffect(() => {
    if (showCart) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [showCart]);

  const handleSearchToggle = () => {
    const willShow = !showMobileSearch;
    setShowMobileSearch(willShow);
    if (willShow) {
      requestAnimationFrame(() => {
        if (searchInputRef.current) searchInputRef.current.focus();
      });
    }
  };

  return (
    <>
      {/* Mobile Coupon Banner */}
      {!isDesktop && showMobileBanner && (
        <div className="fixed top-0 w-full z-50 h-8 bg-black text-white flex items-center justify-center text-xs">
          <CouponBanner />
        </div>
      )}

      {/* Mobile Top Navbar */}
      {!isDesktop && (
        // <div className={`fixed ${showMobileBanner ? 'top-8' : 'top-0'} w-full bg-white text-black flex items-center justify-between px-4 py-3 shadow z-40 h-16 transition-all duration-300`}>
        //   <button onClick={() => setIsSidePanelOpen(true)} className="text-black">
        //     <FaBars size={22} />
        //   </button>

        //   <Link to="/" className="flex-grow text-center">
        //     <img src={logo} alt="Grumist Logo" className="h-12 mx-auto" />
        //   </Link>

        //   <div className="flex items-center space-x-4">
        //     {isScrolled && (
        //       <button onClick={handleSearchToggle} className="text-black">
        //         <FaSearch size={22} />
        //       </button>
        //     )}
        //     <button onClick={() => setShowCart(true)} className="relative text-black">
        //       <FaCartPlus size={22} />
        //       {cartItems.length > 0 && (
        //         <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
        //           {cartItems.length}
        //         </span>
        //       )}
        //     </button>
        //   </div>
        // </div>
        <div className={`fixed ${showMobileBanner ? 'top-8' : 'top-0'} w-full bg-white text-black z-40 h-16 transition-all duration-300 shadow`}>
          <div className="relative flex items-center justify-center h-full">
            {/* Hamburger (left aligned) */}
            <button 
              onClick={() => setIsSidePanelOpen(true)} 
              className="absolute left-4 text-black"
            >
              <FaBars size={22} />
            </button>

            {/* Centered Logo */}
            <Link to="/" className="absolute left-1/2 transform -translate-x-1/2">
              <img src={logo} alt="Glumist Logo" className="h-12" />
            </Link>

            {/* Search + Cart (right aligned) */}
            <div className="absolute right-4 flex items-center space-x-4">
              {isScrolled && (
                <button onClick={handleSearchToggle} className="text-black">
                  <FaSearch size={22} />
                </button>
              )}
              <button onClick={() => setShowCart(true)} className="relative text-black">
                <FaCartPlus size={22} />
                {cartItems.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {cartItems.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

      )}

      {/* Mobile Search Bar */}
      {!isDesktop && showMobileSearch && (
        <div className="fixed top-16 w-full bg-white px-4 py-1 shadow-md z-30 border-t border-gray-100 transition-all duration-300" style={{ top: showMobileBanner ? '98px' : '64px' }}>
          <SearchBar className="w-full" ref={searchInputRef} />
        </div>
      )}

      {/* Desktop Navbar with CouponBanner logic */}
      {isDesktop && (
        <div className={`fixed w-full z-40 bg-white text-black shadow-md transition-all duration-300 ${isDesktopScrolled ? 'top-0' : 'top-8'}`}>
          {/* CouponBanner only when not scrolled */}
          {!isDesktopScrolled && (
            <div className="flex items-center justify-center ">
              <CouponBanner />
            </div>
          )}
          <div className="flex justify-between items-center px-8">
            <div className="flex space-x-8">
              <Link to="/">
                <img src={logo} alt="Glumist Logo" className="h-12 mt-1 w-auto" />
              </Link>
              <div className="font-bold flex space-x-8 py-4">
                <Link to="/products" className={styles.underlineAnim}>All Products</Link>
                <Link to="/about" className={styles.underlineAnim}>About Us</Link>
                <Link to="/contact" className={styles.underlineAnim}>Contact Us</Link>
                {user?.email === "groomberry179@gmail.com" && (
                  <Link to="/admin/addproduct" className="text-red-600">Admin</Link>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-6">
              <SearchBar className="w-[300px]" />
              <button onClick={() => setShowCart(true)} className="relative">
                <FaCartPlus size={22} className="text-black hover:text-black" />
                {cartItems.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {cartItems.length}
                  </span>
                )}
              </button>

              {/* User Dropdown */}
              <div className="relative user-dropdown">
                {user ? (
                  <div 
                    className="flex items-center cursor-pointer"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <FaUserAlt size={22} className="text-black hover:text-black" />
                    <FaChevronDown 
                      size={14} 
                      className={`ml-1 text-black transition-transform ${isDropdownOpen ? 'transform rotate-180' : ''}`}
                    />
                  </div>
                ) : (
                  <button onClick={openLoginModal} className="text-black hover:text-black">
                    <FaUserAlt size={22} />
                  </button>
                )}
                
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 py-1 border border-gray-200">
                    <Link 
                      to="/profile" 
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-purple-50"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <FaUserAlt className="mr-2" />
                      Your Profile
                    </Link>
                    <Link 
                      to="/profile" 
                      state={{ activeTab: 'orders' }} 
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-purple-50"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <FaHistory className="mr-2" />
                      Order History
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setIsDropdownOpen(false);
                      }}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50"
                    >
                      <span className="mr-2">→</span>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Side Panel */}
      {!isDesktop && isSidePanelOpen && (
        <div className="fixed top-0 left-0 w-[70%] h-full bg-white text-black z-50 shadow-md flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between p-4 border-b">
              <img src={logo} alt="Grumist Logo" className="h-12" />
              <button onClick={() => setIsSidePanelOpen(false)}>
                <FaTimes size={20} />
              </button>
            </div>
            <ul className="px-4 space-y-4 text-base mt-8">
              <li><Link to="/products" onClick={() => setIsSidePanelOpen(false)} className="font-bold">New Launch</Link></li>
              <li><Link to="/products" onClick={() => setIsSidePanelOpen(false)} className="font-bold">All Products</Link></li>
              <li><Link to="/about" onClick={() => setIsSidePanelOpen(false)} className="font-bold">About Us</Link></li>
              <li><Link to="/contact" onClick={() => setIsSidePanelOpen(false)} className="font-bold">Contact Us</Link></li>
              <li> {user?.email === "groomberry179@gmail.com" && (
                <Link to="/admin" className="text-red-600">Admin</Link>
              )}</li>
              <li><Link to="/contact" onClick={() => setIsSidePanelOpen(false)} className="text-base text-black underline">Need Help?</Link></li>
            </ul>
          </div>
          {/* <Link to="/profile" onClick={() => setIsSidePanelOpen(false)} className="px-6 py-4 border-t flex items-center space-x-2">
            <FaUserAlt />
            <span>Account</span>
          </Link> */}
          {user && (
            <Link
              to="/profile"
              onClick={() => setIsSidePanelOpen(false)}
              className="px-6 py-4 border-t flex items-center space-x-2"
            >
              <FaUserAlt />
              <span>Account</span>
            </Link>
          )}

        </div>
      )}

      {/* Mobile Bottom Navbar */}
      {!isDesktop && !isSidePanelOpen && (
        <div className="fixed bottom-0 w-full bg-white text-Black flex justify-evenly px-4 py-3 z-40 border-t border-gray-200">
          <div className="flex-1 text-center">
            <Link to="/" className="flex flex-col items-center" onClick={() => setIsDropdownOpen(false)}>
              <FaHome className="mx-auto" size={24} />
              <span className="text-xs mt-1">Home</span>
            </Link>
          </div>

          <div className="flex-1 text-center">
            <Link to="/products" className="flex flex-col items-center" onClick={() => setIsDropdownOpen(false)}>
              <FaBoxOpen className="mx-auto" size={24} />
              <span className="text-xs mt-1">Products</span>
            </Link>
          </div>

          <div className="flex-1 text-center relative">
            <button 
              onClick={() => {
                setShowCart(true);
                setIsDropdownOpen(false);
              }} 
              className="relative w-full flex flex-col items-center justify-center"
            >
              <div className="relative">
                <FaCartPlus size={24} className="text-black" />
                {cartItems.length > 0 && (
                  <span className="absolute -top-2 -right-3 bg-red-600 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {cartItems.length}
                  </span>
                )}
              </div>
              <span className="text-xs mt-1">Cart</span>
            </button>
          </div>

          <div className="flex-1 text-center relative user-dropdown">
            {user ? (
              <>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDropdownOpen(!isDropdownOpen);
                  }}
                  className="w-full flex flex-col items-center"
                >
                  <FaUserAlt className="mx-auto" size={24} />
                  <span className="text-xs mt-1">Profile</span>
                </button>
                
                {isDropdownOpen && (
                  <>
                    <div 
                      className="fixed inset-0 bg-black bg-opacity-30 z-40"
                      onClick={() => setIsDropdownOpen(false)}
                    />
                    
                    <div 
                      className="fixed bottom-16 left-1/2 transform -translate-x-1/2 w-11/12 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="py-1">
                        <Link 
                          to="/profile" 
                          className="flex items-center px-4 py-3 text-gray-700 hover:bg-purple-50"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <FaUserAlt className="mr-3 text-black" />
                          <span>Your Profile</span>
                        </Link>
                        <Link 
                          to="/profile" 
                          state={{ activeTab: 'orders' }} 
                          className="flex items-center px-4 py-3 text-gray-700 hover:bg-purple-50"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <FaHistory className="mr-3 text-black" />
                          <span>Order History</span>
                        </Link>
                        <button
                          onClick={() => {
                            logout();
                            setIsDropdownOpen(false);
                          }}
                          className="flex items-center w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-50"
                        >
                          <span className="mr-3 text-black">→</span>
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <button
                onClick={openLoginModal}
                className="w-full flex flex-col items-center"
              >
                <FaUserAlt className="mx-auto" size={24} />
                <span className="text-xs mt-1">Log In</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Cart Overlay */}
      {showCart && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-50"
          onClick={() => setShowCart(false)}
        />
      )}

      <CartDrawer open={showCart} onClose={() => setShowCart(false)} />
    </>
  );
};

export default Navbar;
